"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { useRouter } from "next/navigation";
import classNames from "@/helpers/classNames";
import sanityImageUrl from "@/helpers/sanityImageUrl";
import Container from "@/components/Container";
import Icon from "@/components/Icon";
import Link from "@/components/Link";
import searchResultHref from "@/helpers/searchResultHref";
import Text from "@/components/Text";
import searchPlaceholder from "@/assets/images/search-placeholder.png";
import { algoliaSearchService } from "@/tools/algolia/search";
import type { SearchResult } from "@/tools/algolia/search";
import styles from "./styles.module.scss";

interface SearchDropdownProps {
  isOpen: boolean;
  featuredSearches?: any;
  onClose?: () => void;
}

const SearchDropdown = ({ isOpen, featuredSearches, onClose }: SearchDropdownProps) => {
  const router = useRouter();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  // DEBUG: Set to isOpen to keep dropdown always open for styling inspection
  const dropdownIsOpen = isOpen;

  // The dropdown stays mounted and is hidden with CSS, so `autoFocus` never
  // fires — focus the input each time it opens, and hand focus back to
  // whatever opened it on close so it isn't stranded on a hidden input.
  useEffect(() => {
    if (isOpen) {
      const { activeElement } = document;
      triggerRef.current =
        activeElement instanceof HTMLElement && activeElement !== document.body
          ? activeElement
          : null;
      inputRef.current?.focus();
      return;
    }

    if (wrapperRef.current?.contains(document.activeElement)) {
      triggerRef.current?.focus();
    }
    triggerRef.current = null;
  }, [isOpen]);

  // The latest search sent, so a slower earlier one can't overwrite its results.
  const latestSearchRef = useRef(0);
  // Read through a ref, not state: depending on `searchResults` rebuilt
  // `performSearch` each time results arrived, and the debounce effect below,
  // which depends on it, then ran the same search again 300ms later. Every
  // query was fetched twice and flashed "Searching..." in between.
  const resultCountRef = useRef(0);
  resultCountRef.current = searchResults.length;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      // Outdates a search still in flight, so it can't bring its results back.
      latestSearchRef.current += 1;
      setSearchQuery("");
      onClose?.();
    }
  };

  const handleClearSearch = () => {
    // Outdates a search still in flight, so it can't bring its results back.
    latestSearchRef.current += 1;
    setSearchQuery("");
    setSearchResults([]);
  };

  // Debounced search function
  const performSearch = useCallback(async (query: string) => {
    const searchId = latestSearchRef.current + 1;
    latestSearchRef.current = searchId;

    if (!query.trim()) {
      // No fade. The suggestions replace the results the moment the box
      // empties, so by the time this runs, 300ms later, they are what a fade
      // would hide: they used to blink out and back in after every clear.
      setSearchResults([]);
      setIsTransitioning(false);
      setIsSearching(false);
      return;
    }

    // Start transition when starting search
    if (resultCountRef.current === 0) {
      setIsTransitioning(true);
    }

    setIsSearching(true);
    try {
      const results = await algoliaSearchService.searchAll(query, 4);
      if (searchId !== latestSearchRef.current) {
        return;
      }

      // Set results immediately (images are included in the search data)
      setSearchResults(results.hits);
      setIsTransitioning(false);
    } catch (error) {
      if (searchId !== latestSearchRef.current) {
        return;
      }
      console.error("Search error:", error);
      setSearchResults([]);
      setIsTransitioning(false);
    } finally {
      if (searchId === latestSearchRef.current) {
        setIsSearching(false);
      }
    }
  }, []);

  // Debounce search with 300ms delay
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      performSearch(searchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, performSearch]);

  const handleSuggestedSearchClick = (searchTerm: string) => {
    const encodedQuery = encodeURIComponent(searchTerm);
    router.push(`/search?q=${encodedQuery}`);
    onClose?.();
  };

  // Use data from Sanity. Suggested searches fall back to defaults; featured
  // pages have no honest default, so with none set the Explore block is hidden.
  const suggestedSearches = featuredSearches?.suggestedSearches?.map(
    (item: any) => item.searchText,
  ) || ["Sponsor A Child", "Read The Latest News & Blog Posts", "Ethical Fashion Guide"];

  const exploreItems =
    featuredSearches?.featuredPages?.map((item: any) => {
      // Get user-defined tags from CMS
      const userTags =
        item.searchTags && item.searchTags.length > 0
          ? item.searchTags.map((tag: any) => tag.title || tag).filter(Boolean)
          : [];

      // Always include content type as first tag
      const contentTypeTag = item.type === "blogPost" ? "Blog Post" : "Page";

      return {
        description: item.searchCopy || "",
        image: item.searchImage?.image?.asset?.url || searchPlaceholder.src,
        slug:
          item.type === "blogPost"
            ? `blog/${item.slug}`
            : item.slug?.startsWith("/")
              ? item.slug.slice(1)
              : item.slug,
        tags: [contentTypeTag, ...userTags],
        title: item.searchTitle || item.title,
      };
    }) ?? [];

  const hasSearchQuery = searchQuery.trim().length > 0;

  return (
    <div
      ref={wrapperRef}
      data-theme="white"
      data-visible={isOpen}
      className={classNames(styles.searchWrapper, { [styles.isOpen]: dropdownIsOpen })}
      tabIndex={-1}
      aria-hidden={!dropdownIsOpen}
      // The closed dropdown is hidden with `opacity: 0`, which leaves its
      // controls in the tab order — `aria-hidden` alone does not remove them.
      // Focus could land on an invisible search input inside a subtree screen
      // readers are told to ignore. `inert` takes it out of both.
      inert={!dropdownIsOpen}
      onKeyDown={handleKeyDown}
    >
      <div className={styles.dropdown}>
        <Container className={styles.container}>
          <div className={styles.searchInputWrapper}>
            <Icon title="magnifyingGlass" className={styles.searchIcon} />
            <input
              ref={inputRef}
              type="text"
              className={styles.searchInput}
              placeholder="Search..."
              aria-label="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {hasSearchQuery && (
              <button
                type="button"
                className={styles.clearButton}
                onClick={handleClearSearch}
                aria-label="Clear search"
              >
                <Icon title="close" />
              </button>
            )}
          </div>

          {!hasSearchQuery ? (
            <div key="suggested-view">
              <div
                className={classNames(styles.suggestedSearches, {
                  [styles.fadeOut]: isTransitioning,
                })}
              >
                <Text
                  text="SUGGESTED SEARCHES"
                  variant="label"
                  size="sm"
                  className={styles.sectionTitle}
                />
                {suggestedSearches.map((search: string, index: number) => (
                  <button
                    type="button"
                    key={index}
                    className={styles.suggestedItem}
                    onClick={() => handleSuggestedSearchClick(search)}
                  >
                    <Text text={search} variant="label" size="md" color="themeFgDefault" />
                    <Icon title="magnifyingGlass" className={styles.itemIcon} />
                  </button>
                ))}
              </div>

              {exploreItems.length > 0 && (
                <div
                  className={classNames(styles.explore, {
                    [styles.fadeOut]: isTransitioning,
                  })}
                >
                  <Text text="EXPLORE" variant="label" size="sm" className={styles.sectionTitle} />
                  <div className={styles.exploreGrid}>
                    {exploreItems.map((item: any, index: number) => (
                      <Link
                        key={index}
                        href={searchResultHref(item.slug)}
                        className={styles.exploreCard}
                        size="none"
                        variant="content"
                        onClick={() => onClose?.()}
                      >
                        <div
                          className={styles.cardImage}
                          style={{ backgroundImage: `url(${sanityImageUrl(item.image, 173)})` }}
                        />
                        <div className={styles.cardContent}>
                          <Text
                            text={item.title}
                            size="md"
                            weight="semibold"
                            className={styles.cardTitle}
                            color="themeFgDefault"
                          />
                          {item.description && (
                            <Text text={item.description} className={styles.cardDescription} />
                          )}
                          <div className={styles.cardTags}>
                            {item.tags.map((tag: string, tagIndex: number) => (
                              <span key={tagIndex} className={styles.tag}>
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div key="results-view">
              <div
                className={classNames(styles.searchResults, {
                  [styles.fadeOut]: isTransitioning && searchResults.length === 0,
                })}
              >
                <Text
                  text="CLOSEST MATCHES"
                  variant="label"
                  size="sm"
                  className={styles.sectionTitle}
                />
                {/* Only while there is nothing to show yet. Over existing results it
                    blanked them for a moment on every keystroke. */}
                {isSearching && searchResults.length === 0 ? (
                  <div className={styles.loadingState}>
                    <Text text="Searching..." variant="label" size="md" />
                  </div>
                ) : searchResults.length > 0 ? (
                  <div className={styles.resultsGrid}>
                    {searchResults.map((result, index) => (
                      <Link
                        key={result.objectID || index}
                        href={searchResultHref(result.url)}
                        className={styles.resultCard}
                        size="none"
                        variant="content"
                        onClick={() => onClose?.()}
                      >
                        <div
                          className={styles.cardImage}
                          style={{
                            backgroundImage: `url(${sanityImageUrl(result.image, 173) || searchPlaceholder.src})`,
                          }}
                        />
                        <div className={styles.cardContent}>
                          <Text
                            text={result.title}
                            size="md"
                            weight="semibold"
                            className={styles.cardTitle}
                          />
                          {result.description && (
                            <Text text={result.description} className={styles.cardDescription} />
                          )}
                          <div className={styles.cardTags}>
                            {/* Always show content type as first tag */}
                            <span key="type" className={styles.tag}>
                              {result.contentType ??
                                (result.type === "page" ? "Page" : "Blog Post")}
                            </span>
                            {/* Show additional tags if they exist */}
                            {result.tags &&
                              result.tags.length > 0 &&
                              result.tags.slice(0, 2).map((tag, tagIndex) => (
                                <span key={tagIndex} className={styles.tag}>
                                  {tag}
                                </span>
                              ))}
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className={styles.noResults}>
                    <Text text="No results found" variant="label" size="md" />
                  </div>
                )}
              </div>

              <button
                type="button"
                className={styles.viewAllButton}
                onClick={() => {
                  const encodedQuery = encodeURIComponent(searchQuery);
                  router.push(`/search?q=${encodedQuery}`);
                  onClose?.();
                }}
              >
                <Text text={`View all results for "${searchQuery}"`} />
                <Icon title="triangleRight" className={styles.chevronIcon} />
              </button>
            </div>
          )}
        </Container>
      </div>
    </div>
  );
};

export default SearchDropdown;
